import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Inbox,
  FileText,
  Briefcase,
  Send,
  CalendarRange,
  BarChart3,
  Activity,
  Settings,
  LogOut,
  ExternalLink,
  UserCog,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { BRAND } from '../data/mockData';
import NotificationsBell from './NotificationsBell';
import GlobalSearch from './GlobalSearch';

// `roles` controls which roles see each link. Editors see Dashboard + Blog only.
const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard', roles: ['admin', 'editor'] },
  { to: '/admin/contacts', label: 'CRM', icon: Inbox, testid: 'admin-nav-crm', roles: ['admin'] },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, testid: 'admin-nav-analytics', roles: ['admin'] },
  { to: '/admin/subscribers', label: 'Subscribers', icon: Users, testid: 'admin-nav-subscribers', roles: ['admin'] },
  { to: '/admin/broadcast', label: 'Broadcast', icon: Send, testid: 'admin-nav-broadcast', roles: ['admin'] },
  { to: '/admin/digest', label: 'Weekly Digest', icon: CalendarRange, testid: 'admin-nav-weekly-digest', roles: ['admin'] },
  { to: '/admin/articles', label: 'Blog Editor', icon: FileText, testid: 'admin-nav-blog-editor', roles: ['admin', 'editor'] },
  { to: '/admin/cases', label: 'Case Studies', icon: Briefcase, testid: 'admin-nav-case-studies', roles: ['admin'] },
  { to: '/admin/team', label: 'Team', icon: UserCog, testid: 'admin-nav-team', roles: ['admin'] },
  { to: '/admin/activity', label: 'Activity', icon: Activity, testid: 'admin-nav-activity', roles: ['admin'] },
  { to: '/admin/settings', label: 'Settings', icon: Settings, testid: 'admin-nav-settings', roles: ['admin', 'editor'] },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Memoised so the sidebar nav doesn't recompute on every render. Cheap
  // win — only re-evaluates when the user object or NAV array changes.
  const visibleNav = useMemo(
    () => NAV.filter((n) => !n.roles || n.roles.includes(user?.role || 'editor')),
    [user?.role]
  );

  const SidebarContent = (
    <>
      <div className="px-6 py-7 border-b border-white/5 flex items-center justify-between">
        <div>
          <img
            src={BRAND.logoFull}
            alt="Grouth"
            className="h-9 w-auto brightness-0 invert"
          />
          <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#83BF42] font-['Inter']">
            Admin Console
          </p>
        </div>
        {/* Close button only visible in the mobile drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-mobile-close"
          className="md:hidden text-white/60 hover:text-white p-2 -mr-2"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleNav.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-['Inter'] transition-all duration-200 ${
                isActive
                  ? 'bg-[#83BF42]/10 text-[#83BF42] border border-[#83BF42]/20'
                  : 'text-white/60 hover:text-white hover:bg-white/5 border border-transparent'
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/5 space-y-1 shrink-0">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-['Inter'] text-white/40 hover:text-white hover:bg-white/5 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          View site
        </a>
        <div className="px-4 py-3 mt-2 rounded-lg bg-white/[0.03]">
          <p className="text-xs text-white/40 font-['Inter'] mb-1">Signed in as</p>
          <p className="text-sm text-white truncate">{user?.email}</p>
          {user?.role && (
            <span
              data-testid="admin-current-role"
              className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-['Inter'] uppercase tracking-wider ${
                user.role === 'admin'
                  ? 'bg-[#83BF42]/15 text-[#83BF42] border border-[#83BF42]/30'
                  : 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
              }`}
            >
              {user.role}
            </span>
          )}
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-['Inter'] text-white/60 hover:text-white hover:bg-red-500/10 hover:text-red-400 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0F0F11] text-white flex" data-testid="admin-layout">
      {/* Desktop sidebar — sticky so logout is always reachable */}
      <aside
        data-testid="admin-sidebar-desktop"
        className="hidden md:flex w-64 shrink-0 bg-[#15151A] border-r border-white/5 flex-col sticky top-0 h-screen"
      >
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <button
            type="button"
            aria-label="Close menu overlay"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <aside className="relative w-72 max-w-[85vw] bg-[#15151A] border-r border-white/5 flex flex-col h-full animate-in slide-in-from-left">
            {SidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col" data-testid="admin-main">
        {/* Sticky top bar with global search + notifications + always-visible logout */}
        <div className="sticky top-0 z-30 bg-[#15151A]/80 backdrop-blur border-b border-white/5 h-14 px-4 md:px-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="admin-mobile-open"
            className="md:hidden text-white/70 hover:text-white p-2 -ml-2"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <GlobalSearch />
          <NotificationsBell />
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout-topbar"
            title="Sign out"
            aria-label="Sign out"
            className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-['Inter'] text-white/70 hover:text-red-400 hover:bg-red-500/10 border border-white/10 hover:border-red-500/30 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
